import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { Component, OnInit, OnDestroy } from '@angular/core'
import FormTrait from 'src/app/traits/form.trait'
import { debounceTime, Subject, Subscription } from 'rxjs'
import { select, Store } from '@ngrx/store'
import {
  CreateCompoundRequest,
  FetchCompoundRequest,
  SetDefaultData,
  UpdateCompoundRequest,
  UpdateField,
} from '../../compounds.actions'
import {
  getCreateCompoundState,
  getUpdateCompoundState,
} from '../../compounds.selectors'
import { isImmutable, Map } from 'immutable'
import Validator from '@shared/validatorjs/src/validator'
import { FetchPropertiesRequest } from '@containers/properties/properties.actions'
import { getPropertiesState } from '@containers/properties/properties.selectors'

@Component({
  selector: 'app-update-compound-general',
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.css'],
})
export class UpdateCompoundGeneralComponent
  extends FormTrait
  implements OnInit, OnDestroy
{
  public compoundId: number
  public isLoading: boolean
  private state$: Subscription
  public isOpen = false
  public propertiesOptions = []
  public propertyAutocomplete$: Subscription
  public onPropertyAutocomplete$ = new Subject<string>()
  public properties$: Subscription
  public properties = []
  public selectedProperties = []

  constructor(
    public store: Store<any>,
    private route: ActivatedRoute,
    public router: Router,
  ) {
    super()
  }

  ngOnInit() {
    this.store.dispatch<any>(new SetDefaultData('update'))
    this.state$ = this.store
      .pipe(select(getUpdateCompoundState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.get('isLoading')
      })
    this.compoundId = parseInt(this.route.snapshot.paramMap.get('id'))

    this.properties$ = this.store
      .pipe(select(getPropertiesState))
      .subscribe((state) => {
        this.properties = state.get('data').toJS()
        this.propertiesOptions = this.properties.map(
          ({ propertyCodeAssigned, id }) => ({
            name: propertyCodeAssigned,
            id: id,
          }),
        )
      })

    this.propertyAutocomplete$ = this.onPropertyAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        if (keyword !== null) {
          this.store.dispatch<any>(
            new FetchPropertiesRequest(null, {
              filters: {
                or: [{ field: 'propertyCodeAssigned', value: keyword }],
                nullOr: ['compound'],
              },
              pageSize: 50,
            }),
          )
        }
      })

    this.store.dispatch(new FetchCompoundRequest(this.compoundId))
    this.store.dispatch<any>(
      new FetchPropertiesRequest(null, {
        filters: {
          nullOr: ['compound'],
        },
        pageSize: 50,
      }),
    )
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.propertyAutocomplete$.unsubscribe()
    this.properties$.unsubscribe()
  }

  public makePrimary(row: any) {
    this.store.dispatch(
      new UpdateField(
        'compoundProperties',
        this.getField(['compoundProperties'])
          .toJS()
          .map(({ propertyId, propertyCode }) => ({
            propertyCode,
            propertyId,
            isPrimary: row.get('propertyId') === propertyId,
          })),
        'update.data',
      ),
    )

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state.get('validationErrors').delete('compoundProperties'),
        'update',
      ),
    )
  }

  public deleteProperty(row: any) {
    this.store.dispatch(
      new UpdateField(
        'compoundProperties',
        this.getField(['compoundProperties'])
          .toJS()
          .filter(({ propertyId }) => row.get('propertyId') !== propertyId),
        'update.data',
      ),
    )
  }

  public compareFn(item: any, selected: any) {
    return item.id == selected.id
  }

  public handleFieldChange(e: any, name: null | string = null): void {
    if (name === 'compoundProperties') {
      this.store.dispatch(
        new UpdateField(
          name,
          [
            ...this.getField(['compoundProperties']),
            ...e.map(({ id: propertyId, name: propertyCode }) => ({
              propertyId,
              propertyCode,
              isPrimary: false,
            })),
          ],
          'update.data',
        ),
      )
      this.isOpen = false
      this.selectedProperties = []
    } else {
      this.store.dispatch(
        new UpdateField(
          name || e.target.name,
          name ? e : e.target.value,
          'update.data',
        ),
      )
    }

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state.get('validationErrors').delete(name || e.target.name),
        'update',
      ),
    )
  }

  validation() {
    const data = this.state.get('data')
    const rules = {
      name: 'required',
      compoundProperties: 'primary',
    }
    Validator.register(
      'primary',
      function (value, requirement, attribute) {
        return value.reduce((prev, curr) => prev || curr.isPrimary, false)
      },
      'The :attribute must contain a primary property.',
    )

    const validator = new Validator(data.toJS(), rules)

    validator.setAttributeNames({
      compoundProperties: 'properties',
    })

    return validator
  }

  public onSubmit(e: Event): void {
    e.preventDefault()
    const validation = this.validation()
    const payload = this.state.get('data').toJS()

    if (validation.passes()) {
      this.store.dispatch(new UpdateCompoundRequest(this.compoundId, payload))
    } else {
      this.store.dispatch<any>(
        new UpdateField('validationErrors', validation.errors.errors, 'update'),
      )
    }
  }
}
